import { useRef, useState } from 'react';
import { curveBetween } from './curveGeometry';
import type { Box } from './curveGeometry';

export interface DrawnCurve {
  key: string;
  from: Box;
  to: Box;
}

/**
 * Connections, drawn under every card in one SVG layer: a soft S and a dot at
 * the target end, no labels. A pressed curve is selected, and the page removes
 * a selected one on Delete. With [onRemove], hovering a curve darkens it and
 * puts a × at its middle.
 */
export function Curves({
  curves,
  selected = null,
  onSelect,
  onRemove,
}: {
  curves: DrawnCurve[];
  selected?: string | null;
  onSelect?: (key: string) => void;
  onRemove?: (key: string) => void;
}) {
  const [hovered, setHovered] = useState<string | null>(null);
  const leaving = useRef<ReturnType<typeof setTimeout> | null>(null);

  /** Leaving waits a moment, so the pointer can cross from the curve to its ×. */
  const hover = (key: string | null) => {
    if (leaving.current) clearTimeout(leaving.current);
    leaving.current = null;
    if (key !== null) setHovered(key);
    else leaving.current = setTimeout(() => setHovered(null), 150);
  };

  if (curves.length === 0) return null;

  const drawn = curves.map((c) => ({ key: c.key, ...curveBetween(c.from, c.to) }));
  const removable = onRemove ? drawn.find((c) => c.key === hovered) : undefined;

  return (
    <>
      <svg className="edges" aria-hidden="true">
        {drawn.map((c) => (
          <g
            key={c.key}
            className={[c.key === selected && 'on', onRemove && c.key === hovered && 'hot'].filter(Boolean).join(' ') || undefined}
          >
            {/* A thin line is a thin target. The invisible one underneath is
                what a pointer actually hits. */}
            <path
              d={c.d}
              className="edge-hit"
              onPointerDown={(event) => {
                event.stopPropagation();
                onSelect?.(c.key);
              }}
              onPointerEnter={() => hover(c.key)}
              onPointerLeave={() => hover(null)}
            />
            <path d={c.d} className="edge" />
            <circle cx={c.end.x} cy={c.end.y} r="3.5" className="edge-dot" />
          </g>
        ))}
      </svg>
      {removable && onRemove && (
        <button
          type="button"
          className="curve-remove"
          style={{ left: removable.mid.x, top: removable.mid.y }}
          aria-label="Remove this connection"
          onPointerEnter={() => hover(removable.key)}
          onPointerLeave={() => hover(null)}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => {
            event.stopPropagation();
            onRemove(removable.key);
            setHovered(null);
          }}
        >
          <span aria-hidden="true">×</span>
        </button>
      )}
    </>
  );
}
